import React, {
	useEffect,
	useState,
} from 'react';
import {
	Col,
	Flex,
	Grid,
	Row,
	Typography,
} from 'antd';
import type { GetVesselDetailsResponse } from '@api/features/vessels/getVesselDetails';
import type { CargoTypeProps } from '@api/models/cargo-type';
import { updateVessel } from '@client/lib/api';
import { useEstimateChangesProvider } from '@client/screens/estimates/details/context/EstimateChangesContext';
import { useEstimate } from '@client/screens/estimates/details/context/EstimatorContext';
import { useCargoProvider } from '@client/screens/estimates/details/context/CargoContext';
import { usePortRotationProvider } from '@client/screens/estimates/details/context/PortRotationContext';
import Modal from '@client/components/Modal/Modal';
import { useSaveButton } from '@client/utils/hooks/useSaveButton';
import { useNavigationBlock } from '@client/lib/navigationBlock';
import IntakeCalculatorPortRotation from '../IntakeCalculatorPortRotation/IntakeCalculatorPortRotation';
import IntakeCalculatorFreightDetails from '../IntakeCalculatorFreightCard/IntakeCalculatorFreightDetails';
import IntakeCalculatorCargosTable from './IntakeCalculatorCargosTable';
import IntakeCalculatorKeyDetails from './IntakeCalculatorKeyDetails';
import styles from './IntakeCalculatorModal.module.css';
import IntakeCalculatorBars from './IntakeCalculatorBars';

type Props = {
	visible: boolean;
	setVisible: () => void;
	cargoTypes: CargoTypeProps[];
};

const IntakeCalculatorModal = ({
	visible,
	setVisible,
	cargoTypes,
}: Props) => {
	const [localVessel, setLocalVessel] = useState<GetVesselDetailsResponse | null>(null);
	const [pendingVesselChanges, setPendingVesselChanges] = useState({});
	const [showAllPorts, setShowAllPorts] = useState(false);
	const screens = Grid.useBreakpoint();

	const { portRotation, intakePortRotation } = usePortRotationProvider();
	const visiblePortRotation = showAllPorts ? portRotation : intakePortRotation;

	const {
		makeBlockable,
		useBlocker,
	} = useNavigationBlock();

	const {
		cargos,
	} = useCargoProvider();

	const {
		onUpdateEstimate,
		estimate,
		vessel,
	} = useEstimate();

	const cargoDetails = cargos[0];

	const onUpdateVessel = async (attributes: Record<string, any>) => {
		setLocalVessel((prev) => {
			if (prev == null) {
				return null;
			}

			return { ...prev, ...attributes };
		});

		setPendingVesselChanges((prev) => {
			return { ...prev, ...attributes };
		});
	};

	useEffect(() => {
		if (vessel != null) {
			setLocalVessel(vessel);
		}
	}, [vessel]);

	const {
		onSaveChanges,
		hasPendingChanges: hasPendingEstimateChanges,
		saving,
	} = useEstimateChangesProvider();

	const onSave = async () => {
		if (estimate?.vesselId != null && Object.keys(pendingVesselChanges).length > 0) {
			await updateVessel(estimate.vesselId, pendingVesselChanges, estimate.id);
			setPendingVesselChanges({});
		}

		await onSaveChanges();
	};

	const hasPendingChanges = visible && (
		hasPendingEstimateChanges ||
		Object.keys(pendingVesselChanges).length > 0
	);

	useBlocker(hasPendingChanges);

	const saveButton = useSaveButton({
		title: 'Save & Calculate',
		onSave,
		hasPendingChanges,
		loading: saving,
		blockNavigation: hasPendingChanges
	});

	const closeModal = () => {
		setVisible();
	};

	const safeClose = makeBlockable(closeModal);

	return (
		<Modal
			title={(
				<Flex align="center" justify="space-between">
					<Typography.Title level={3}>Intake calculator</Typography.Title>
					{saveButton}
				</Flex>
			)}
			closable={false}
			onCancel={safeClose}
			footer={null}
			open={visible}
			destroyOnClose
			width={`calc(100vw - ${screens.lg ? '15%' : '5%'})`}
			className={styles.modal}
		>
			<Row gutter={[16, 16]}>
				<Col span={screens.lg ? 12 : 24}>
					<IntakeCalculatorKeyDetails
						handleVesselChange={onUpdateVessel}
						handleEstimateChange={onUpdateEstimate}
						vesselDetails={localVessel}
					/>
				</Col>
				<Col span={screens.lg ? 12 : 24}>
					<IntakeCalculatorFreightDetails
						cargoId={cargoDetails?.id}
					/>
				</Col>
				<Col span={24}>
					<IntakeCalculatorCargosTable
						cargoTypes={cargoTypes}
						showAllPorts={showAllPorts}
						setShowAllPorts={setShowAllPorts}
					/>
				</Col>
				<Col span={24}>
					<IntakeCalculatorPortRotation portRotation={visiblePortRotation} />
				</Col>
				<Col span={24}>
					<IntakeCalculatorBars portRotation={visiblePortRotation} />
				</Col>
			</Row>
		</Modal>
	);
};

export default IntakeCalculatorModal;
